import { createContext, useContext, type ReactNode } from 'react';
import type { SiteContent } from '@personal-portfolio/contract';

/**
 * Editorial copy comes from the CMS and nowhere else — there is no bundled
 * fallback. So the app resolves it once, at the root, and only renders the
 * tree when it has it. Everything below can then read copy synchronously and
 * treat it as always-present, which is what keeps ~40 call sites free of
 * loading branches.
 *
 * The gate that fills this (splash while pending, retry on failure) is UI and
 * lives in the app; this file is only the seam.
 */
const SiteContentContext = createContext<SiteContent | undefined>(undefined);

export function SiteContentProvider({
  value,
  children,
}: {
  value: SiteContent;
  children: ReactNode;
}) {
  return (
    <SiteContentContext.Provider value={value}>
      {children}
    </SiteContentContext.Provider>
  );
}

/**
 * Editorial copy for the whole site. Never undefined: the provider above only
 * renders children once the CMS has answered, so a missing context means the
 * component was mounted outside the gate — a wiring bug, not a loading state.
 */
export const useSiteContent = (): SiteContent => {
  const content = useContext(SiteContentContext);
  if (!content) {
    throw new Error(
      'useSiteContent must be used inside <SiteContentProvider>. The app gate ' +
        'renders it once the CMS copy has loaded.',
    );
  }
  return content;
};
